/**
 * The AUDIENCE surface: "your audience and reaching them". It hosts the subscribers
 * list (add / import / remove) and the Send panel (send a test, then send to your
 * confirmed subscribers). Both were previously on the combined Operate surface; the
 * navigation-IA rework split Operate into Audience (people + sending) and Basics
 * (setup + schedule + delete). Everything reads the shared workspace context, so the
 * hoisted subscriber cache and the drafted issue are reused, never refetched, exactly
 * as before.
 */
import { useAuth } from '../../auth/context';
import { SendPanel, SentIssuesPanel, SubscribersPanel } from './panels';
import { useNewsletterWorkspace } from './workspace';

export function AudienceView(): React.JSX.Element {
  const {
    deps,
    newsletter,
    issueCache,
    subscribersCache,
    setSubscribersCache,
    subscriberEmail,
    setSubscriberEmail,
  } = useNewsletterWorkspace();
  const { user } = useAuth();

  return (
    <div className="nlv__section-narrow">
      <div className="nlv__layout">
        {/* The list first: build an audience, then reach it. */}
        <div className="nlv__subscribers-area">
          <SubscribersPanel
            deps={deps}
            newsletterId={newsletter.newsletterId}
            cache={subscribersCache}
            onCacheChange={setSubscribersCache}
            email={subscriberEmail}
            onEmailChange={setSubscriberEmail}
          />
        </div>

        {/* Sending reuses the already-loaded subscriber cache for the confirmed count and
            the hoisted draft issue, so nothing is refetched here. The studio (where the
            issue is drafted) is the sibling index route, one level up. */}
        <div className="nlv__send-area">
          <SendPanel
            deps={deps}
            newsletterId={newsletter.newsletterId}
            subscribers={subscribersCache.subscribers}
            subscribersReady={subscribersCache.state === 'ready'}
            issue={issueCache.issue}
            {...(user !== null ? { ownerEmail: user.email } : {})}
            designTo=".."
          />

          {/* A read-only record of what actually went out, below the send panel. */}
          <SentIssuesPanel deps={deps} newsletterId={newsletter.newsletterId} />
        </div>
      </div>
    </div>
  );
}
